import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';
import AssetPreview from 'src/components/asset-preview';
import { EldDriverStatusLabel } from 'src/components/eld-driver-status-label';
import { DeviceWithMapInfo, EldEngineImmobilizer, EldVehicle, EldDriverLogGroup, EldVehicleStats, DeviceEldDriver, SafetyTripFull } from 'src/models';
import { formatDatetimeForPreview, formatTimeAsHourMin, today } from 'src/utils/date-utils';
import { overSpeed, getDeviceDrivers, getLabelFromOrigin } from 'src/utils/device-utils';
import { usePrevious } from 'src/hooks/use-previous';
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { formatAtMost2Decimals } from '../eld/eld-vehicles/_eld_vehicle_modal';

export type VehicleInfoProps = {
  deviceEldDrivers: DeviceEldDriver[],
  selectedDevice: DeviceWithMapInfo;
  handleShareLocationClick: (vehicle?: DeviceWithMapInfo) => void;
  onClose: () => void;
  timezone: string;
  eldEngineImmobilizer?: EldEngineImmobilizer;
  eldVehicles: EldVehicle[];
  setDeviceForHistory: (device: DeviceWithMapInfo) => void;
  setViewingMode: (mode: "sidebar" | "history") => void;
  eldDriverLogGroups?: EldDriverLogGroup[];
  setHoveredTrip: (trip: SafetyTripFull | undefined) => void;
  hoveredTrip: SafetyTripFull | undefined;
};

export default function VehicleInfo({
  deviceEldDrivers,
  selectedDevice,
  handleShareLocationClick,
  onClose,
  timezone,
  eldEngineImmobilizer,
  eldVehicles,
  setDeviceForHistory,
  setViewingMode,
  eldDriverLogGroups,
  setHoveredTrip,
  hoveredTrip,
}: VehicleInfoProps) {
  const router = useRouter();
  const [sensorsExpanded, setSensorsExpanded] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(false);
  const prevDiagnosticsExpanded = usePrevious(diagnosticsExpanded)
  const [currentVehicleStatsLoading, setCurrentVehicleStatsLoading] = useState(false);
  const [currentVehicleStats, setCurrentVehicleStats] = useState<EldVehicleStats | null>(null);

  // Trips accordion state
  const [tripsExpanded, setTripsExpanded] = useState(false);
  const prevTripsExpanded = usePrevious(tripsExpanded);
  const [safetyTripsLoading, setSafetyTripsLoading] = useState(false);
  const [safetyTrips, setSafetyTrips] = useState<SafetyTripFull[] | null>(null);

  const vehicleInfo = useMemo(() => {
    if (!eldVehicles || eldVehicles.length === 0) return null;
    const matchedVehicle = eldVehicles.find(vehicle => vehicle.deviceId === selectedDevice?.id);
    return matchedVehicle || null;
  }, [selectedDevice, eldVehicles]);
  const vehicleVin = vehicleInfo?.vin;

  useEffect(() => {
    if (!prevDiagnosticsExpanded && diagnosticsExpanded && vehicleInfo?.id) {
      setCurrentVehicleStats(null);
      setCurrentVehicleStatsLoading(true);

      axios.get(formatEndpoint(endpoints.eldVehicleStats.findByEldVehicleId, { eldVehicleId: vehicleInfo.id }))
        .then(response => {
          setCurrentVehicleStats(response.data);
        })
        .catch(() => {
          setCurrentVehicleStats(null);
        })
        .finally(() => {
          setCurrentVehicleStatsLoading(false);
        });
    }
  }, [diagnosticsExpanded, prevDiagnosticsExpanded, vehicleInfo?.id])

  const [vehicleTripDate, setVehicleTripDate] = useState(today(timezone));

  const prevVehicleTripDate = usePrevious(vehicleTripDate);

  // Lazy load safety trips when accordion is expanded
  useEffect(() => {
    if (((!prevTripsExpanded && tripsExpanded) || (tripsExpanded && vehicleTripDate !== prevVehicleTripDate)) && selectedDevice?.id) {
      setSafetyTrips(null);
      setSafetyTripsLoading(true);

      axios.get(formatEndpoint(endpoints.safetyTrips.findByDeviceId, { deviceId: selectedDevice.id, date: vehicleTripDate }))
        .then(response => {
          setSafetyTrips(response.data);
        })
        .catch(() => {
          setSafetyTrips(null);
        })
        .finally(() => {
          setSafetyTripsLoading(false);
        });
    }
  }, [tripsExpanded, prevTripsExpanded, selectedDevice?.id, vehicleTripDate, prevVehicleTripDate]);

  if (!selectedDevice) {
    return null;
  }
  const device = selectedDevice;


  const getDriverLogGroup = (driverId: number) => eldDriverLogGroups?.find(log => log.eldDriverId === driverId);

  const isTruck = device.deviceType.startsWith("ELD Truck");

  const eldDriversForDevice = getDeviceDrivers(deviceEldDrivers, device);



  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 1,
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#f8f9fa",
        minHeight: "48px"
      }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
          Vehicle Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Iconify icon="solar:arrow-left-linear" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <Box sx={{ textAlign: "center", p: { xs: 1, md: 1.5 }, borderBottom: "1px solid #e0e0e0" }}>
          <AssetPreview device={device} />

          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5, fontSize: { xs: "14px", md: "16px" } }}>
            {device.deviceType}
          </Typography>

          {vehicleInfo && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: "10px", md: "11px" }, lineHeight: 1.3 }}>
              <strong>Vehicle:</strong> {vehicleInfo.vehicleUnitNumber}
              {vehicleInfo.vehicleMake && vehicleInfo.vehicleModel && (
                <br />
              )}
              {vehicleInfo.vehicleMake && vehicleInfo.vehicleModel && (
                <span>{vehicleInfo.vehicleMake} {vehicleInfo.vehicleModel}</span>
              )}
              {vehicleInfo.vehicleYear && (
                <span> ({vehicleInfo.vehicleYear})</span>
              )}
            </Typography>
          )}

          {vehicleInfo?.licensePlate && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: "10px", md: "11px" } }}>
              <strong>License:</strong> {vehicleInfo.licensePlate}
              {vehicleInfo.licensePlateState && (
                <span> ({vehicleInfo.licensePlateState})</span>
              )}
            </Typography>
          )}

          <Stack spacing={{ xs: 0.75, md: 1 }} sx={{ px: { xs: 0.5, md: 0 }, mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="ic:baseline-share" />}
              onClick={() => {
                handleShareLocationClick(device);
              }}
              fullWidth
              size="small"
              sx={{ fontSize: { xs: "11px", md: "12px" }, height: { xs: "30px", md: "32px" }, "&:hover": { backgroundColor: "#aadb26" } }}
            >
              Live Share
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="mdi:history" />}
              onClick={() => {
                setDeviceForHistory(device);
                setViewingMode("history");
              }}
              fullWidth
              size="small"
              sx={{ fontSize: { xs: "11px", md: "12px" }, height: { xs: "30px", md: "32px" }, "&:hover": { backgroundColor: "#aadb26" } }}
            >
              Device History
            </Button>
          </Stack>
        </Box>

        {isTruck && (
          <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
            <Box sx={{ p: { xs: 1, md: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ fontSize: { xs: "13px", md: "14px" }, fontWeight: "600", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                  <Iconify icon="mdi:account-group" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                  Drivers ({eldDriversForDevice.length || 0})
                </Box>
              </Typography>
              {(!eldDriversForDevice || eldDriversForDevice.length === 0) ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                  No drivers assigned to this vehicle
                </Typography>
              ) : (
                <List disablePadding>
                  {eldDriversForDevice.map((driverItem: any, index: number) => {
                    const driverLogGroup = getDriverLogGroup(driverItem.id);
                    return (
                      <Box key={driverItem.id || index}>
                        <ListItem disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>
                                    {driverItem.firstName && driverItem.lastName
                                      ? `${driverItem.firstName} ${driverItem.lastName}`
                                      : driverItem.name || 'Unknown Driver'
                                    }
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {driverLogGroup?.lastStatus && (
                                      <EldDriverStatusLabel
                                        status={driverLogGroup.lastStatus}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                {driverLogGroup && (
                                  <Box
                                    onClick={() => router.push(`/app/eld/eld-driver-logs/driver/${driverItem.id}`)}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      mt: 0.5,
                                      cursor: "pointer",
                                      borderRadius: 1,
                                      p: 0.5,
                                      mx: -0.5,
                                      "&:hover": { backgroundColor: "#f5f5f5" },
                                      "&:hover .driver-log-arrow": { opacity: 1 },
                                    }}
                                  >
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.5, flex: 1 }}>
                                      <Box sx={{ textAlign: "center" }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                          BREAK
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                          {formatTimeAsHourMin(driverLogGroup.breakTime)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ textAlign: "center" }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                          DRIVE
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                          {formatTimeAsHourMin(driverLogGroup.driveTime)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ textAlign: "center" }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                          SHIFT
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                          {formatTimeAsHourMin(driverLogGroup.shiftTime)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ textAlign: "center" }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                          CYCLE
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                          {formatTimeAsHourMin(driverLogGroup.cycleTime)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Iconify
                                      className="driver-log-arrow"
                                      icon="mdi:chevron-right"
                                      width={16}
                                      height={16}
                                      sx={{ opacity: 0, transition: "opacity 0.2s", color: "text.secondary" }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < (eldDriversForDevice?.length || 0) - 1 && <Divider />}
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
          <Box
            sx={{
              p: { xs: 1, md: 1.5 },
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f5f5f5" }
            }}
            onClick={() => setSensorsExpanded(!sensorsExpanded)}
          >
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: { xs: "13px", md: "14px" }, fontWeight: "600" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                <Iconify icon="mdi:gauge" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                Sensors
              </Box>
              <Iconify
                icon={sensorsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                sx={{ transition: "transform 0.2s" }}
                width={{ xs: 14, md: 16 }}
                height={{ xs: 14, md: 16 }}
              />
            </Typography>
          </Box>
          {sensorsExpanded && (
            <Box sx={{ p: { xs: 0.5, md: 1 } }}>
              <Stack spacing={0}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Speed</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "11px", md: "12px" },
                      color: device.velocity && overSpeed(device) ? "#ff5631" : "#4CAF50"
                    }}
                  >
                    {device.velocity ? `${device.velocity.toFixed(1)} mph` : "0 mph"}
                  </Typography>
                </Box>

                {eldEngineImmobilizer && eldEngineImmobilizer.speed !== undefined && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>ELD Speed</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                      {eldEngineImmobilizer.speed} mph
                    </Typography>
                  </Box>
                )}

                {device.externalIdentifier && !device.externalIdentifier.startsWith("2|") && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>Battery</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px", color: device.battery > 20 ? "#4CAF50" : "#ff5631" }}>
                      {device.battery}%
                    </Typography>
                  </Box>
                )}

                {eldEngineImmobilizer?.battery && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>ELD Battery</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                      {eldEngineImmobilizer.battery}
                    </Typography>
                  </Box>
                )}

                {device.direction !== null && device.direction !== undefined && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>Direction</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                      {device.direction}°
                    </Typography>
                  </Box>
                )}

                {eldEngineImmobilizer && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>GPS Status</Typography>
                    <Chip
                      label={eldEngineImmobilizer.gpsFixed ? "Fixed" : "Not Fixed"}
                      color={eldEngineImmobilizer.gpsFixed ? "success" : "warning"}
                      size="small"
                      sx={{ fontSize: "10px", height: "18px" }}
                    />
                  </Box>
                )}

                {eldEngineImmobilizer && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>Power</Typography>
                    <Chip
                      label={eldEngineImmobilizer.powerConnected ? "On" : "Off"}
                      color={eldEngineImmobilizer.powerConnected ? "success" : "error"}
                      size="small"
                      sx={{ fontSize: "10px", height: "18px" }}
                    />
                  </Box>
                )}

                {isTruck && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>Odometer {device.origin ? `(${getLabelFromOrigin(device.origin)})` : ""}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                      {device.lastOdometer.toFixed(2)} mi
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>Last Ping</Typography>
                  <Typography variant="body2" sx={{ fontSize: "11px" }} color="text.secondary">
                    {formatDatetimeForPreview(device.lastPing, timezone)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>

        <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
          <Box
            sx={{
              p: { xs: 1, md: 1.5 },
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f5f5f5" }
            }}
            onClick={() => setDetailsExpanded(!detailsExpanded)}
          >
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: { xs: "13px", md: "14px" }, fontWeight: "600" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                <Iconify icon="mdi:clipboard-text" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                Details
              </Box>
              <Iconify
                icon={detailsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                sx={{ transition: "transform 0.2s" }}
                width={{ xs: 14, md: 16 }}
                height={{ xs: 14, md: 16 }}
              />
            </Typography>
          </Box>
          {detailsExpanded && (
            <Box sx={{ p: 1 }}>
              <Stack spacing={0}>
                <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                    External Identifier
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px", wordBreak: "break-all" }}>
                    {device.externalIdentifier || "N/A"}
                  </Typography>
                </Box>

                {vehicleVin && (
                  <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                      Vehicle VIN
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "monospace", fontSize: "11px", wordBreak: "break-all" }}>
                      {vehicleVin}
                    </Typography>
                  </Box>
                )}

                {eldEngineImmobilizer?.simId && (
                  <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                      SIM ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "11px", wordBreak: "break-all" }}>
                      {eldEngineImmobilizer.simId}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                    Device Type
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    {device.deviceType}
                  </Typography>
                </Box>

                {eldEngineImmobilizer?.dateActivated && (
                  <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                      Date Activated
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: "12px" }}>
                      {formatDatetimeForPreview(eldEngineImmobilizer.dateActivated, timezone)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "10px", md: "11px" } }}>
                    Current Location
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: "11px", md: "12px" } }}>
                    {device.location || "Unknown"}
                  </Typography>
                </Box>

                <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "10px", md: "11px" } }}>
                    Device Coordinates
                  </Typography>
                  <Link
                    href={`https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: { xs: "10px", md: "11px" },
                      textDecoration: "none",
                      color: "blue",
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    {device.lastPosLat}, {device.lastPosLong}
                  </Link>
                </Box>

                {eldEngineImmobilizer && eldEngineImmobilizer.posLat && eldEngineImmobilizer.posLong &&
                  (eldEngineImmobilizer.posLat !== device.lastPosLat || eldEngineImmobilizer.posLong !== device.lastPosLong) && (
                    <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "10px", md: "11px" } }}>
                        ELD Coordinates
                      </Typography>
                      <Link
                        href={`https://maps.google.com/?q=${eldEngineImmobilizer.posLat},${eldEngineImmobilizer.posLong}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: { xs: "10px", md: "11px" },
                          textDecoration: "none",
                          color: "blue",
                          "&:hover": {
                            textDecoration: "underline"
                          }
                        }}
                      >
                        {eldEngineImmobilizer.posLat}, {eldEngineImmobilizer.posLong}
                      </Link>
                    </Box>
                  )}

                {device.lastDormancyReported && (
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Dormancy Reported
                    </Typography>
                    <Typography variant="body1">
                      {formatDatetimeForPreview(device.lastDormancyReported, timezone)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Box>

        {isTruck && (
          <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                p: { xs: 1, md: 1.5 },
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" }
              }}
              onClick={() => setDiagnosticsExpanded(!diagnosticsExpanded)}
            >
              <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: { xs: "13px", md: "14px" }, fontWeight: "600" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                  <Iconify icon="mdi:wrench" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                  Diagnostics
                </Box>
                <Iconify
                  icon={diagnosticsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                  sx={{ transition: "transform 0.2s" }}
                  width={{ xs: 14, md: 16 }}
                  height={{ xs: 14, md: 16 }}
                />
              </Typography>
            </Box>
            {diagnosticsExpanded && (
              <Box sx={{ p: { xs: 0.5, md: 1 } }}>
                <Stack spacing={0}>
                  {currentVehicleStatsLoading ? <CircularProgress /> : <>
                    {currentVehicleStats && (
                      currentVehicleStats.coolantLevelPercent > 0 ||
                      currentVehicleStats.coolantTemperature > 0 ||
                      currentVehicleStats.fuelLevelPercent > 0 ||
                      currentVehicleStats.oilLevelPercent > 0 ||
                      currentVehicleStats.oilTemperature > 0 ||
                      (currentVehicleStats.dtcCodesParsed && currentVehicleStats.dtcCodesParsed.length > 0)
                    ) ? (
                      <>
                        {currentVehicleStats.coolantLevelPercent > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Coolant Level</Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                              {formatAtMost2Decimals(currentVehicleStats.coolantLevelPercent)}%
                            </Typography>
                          </Box>
                        )}

                        {currentVehicleStats.coolantTemperature > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Coolant Temperature</Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                              {formatAtMost2Decimals(currentVehicleStats.coolantTemperature)}°C
                            </Typography>
                          </Box>
                        )}

                        {currentVehicleStats.fuelLevelPercent > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Fuel Level</Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                              {formatAtMost2Decimals(currentVehicleStats.fuelLevelPercent)}%
                            </Typography>
                          </Box>
                        )}

                        {currentVehicleStats.oilLevelPercent > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Oil Level</Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                              {formatAtMost2Decimals(currentVehicleStats.oilLevelPercent)}%
                            </Typography>
                          </Box>
                        )}

                        {currentVehicleStats.oilTemperature > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Oil Temperature</Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                              {formatAtMost2Decimals(currentVehicleStats.oilTemperature)}°C
                            </Typography>
                          </Box>
                        )}

                        {currentVehicleStats.dtcCodesParsed && currentVehicleStats.dtcCodesParsed.length > 0 && (
                          <Box sx={{ py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, mb: 1, fontWeight: 600 }}>
                              Diagnostic Codes ({currentVehicleStats.dtcCodesParsed.length})
                            </Typography>
                            <Box sx={{ py: { xs: 0.5, md: 0.75 } }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: { xs: "11px", md: "12px" },
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                SPN: {currentVehicleStats.dtcCodesParsed[0].spn} - {currentVehicleStats.dtcCodesParsed[0].spnDesc}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "10px", md: "11px" },
                                  color: "#666",
                                  display: "block",
                                }}
                              >
                                FMI: {currentVehicleStats.dtcCodesParsed[0].fmi} ({currentVehicleStats.dtcCodesParsed[0].fmiDesc})
                                {currentVehicleStats.dtcCodesParsed[0].occurrence > 1 && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ fontSize: { xs: "10px", md: "11px" }, color: "#e65100", fontWeight: 500, ml: 0.5 }}
                                  >
                                    × {currentVehicleStats.dtcCodesParsed[0].occurrence} occurrences
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                            {currentVehicleStats.dtcCodesParsed.length > 1 && (
                              <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Iconify icon="mdi:information-outline" width={14} height={14} sx={{ color: "#666" }} />
                                <Typography variant="caption" sx={{ fontSize: { xs: "10px", md: "11px" }, color: "#666" }}>
                                  +{currentVehicleStats.dtcCodesParsed.length - 1} more faults seen in the last 3 days
                                </Typography>
                              </Box>
                            )}
                            <Box sx={{ mt: 1 }}>
                              <Link
                                component="button"
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "10px", md: "11px" },
                                  color: "#1976d2",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                  fontWeight: 500,
                                  "&:hover": { textDecoration: "underline" },
                                }}
                                onClick={() => {
                                  const vin = vehicleInfo?.vin || '';
                                  router.push(`${paths.app.eldVehicleDtcHistory.list}${vin ? `?search=${encodeURIComponent(vin)}` : ''}`);
                                }}
                              >
                                View History
                              </Link>
                            </Box>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{ py: { xs: 1, md: 1.5 }, px: { xs: 0.5, md: 1 }, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "11px", md: "12px" } }}>
                          No vehicle statistics available
                        </Typography>
                      </Box>
                    )}
                  </>}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* {isTruck && (
          <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                p: { xs: 1, md: 1.5 },
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" }
              }}
              onClick={() => setTripsExpanded(!tripsExpanded)}
            >
              <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: { xs: "13px", md: "14px" }, fontWeight: "600" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                  <Iconify icon="mdi:road-variant" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                  Trips
                </Box>
                <Iconify
                  icon={tripsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                  sx={{ transition: "transform 0.2s" }}
                  width={{ xs: 14, md: 16 }}
                  height={{ xs: 14, md: 16 }}
                />
              </Typography>
            </Box>


            {tripsExpanded && <>
              {!safetyTripsLoading && <Box sx={{pl: 4.575}}>
                <TruxSimpleDateField small value={vehicleTripDate} onChange={(newDate) => newDate && setVehicleTripDate(newDate)} />
              </Box>}
              <Box>
                {safetyTripsLoading ? (
                  <Box sx={{ p: { xs: 0.5, md: 1 } }}>
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  </Box>
                ) : (
                  <>
                    {safetyTrips && safetyTrips.length > 0 ? (
                      <Stack>
                        {safetyTrips.toReversed().map((trip, index) => (
                          <SafetyTripRow key={index} trip={trip} index={index} hoveredTrip={hoveredTrip} setHoveredTrip={setHoveredTrip} safetyTrips={safetyTrips} timezone={timezone} />
                        ))}
                      </Stack>
                    ) : (
                      <Box sx={{ py: { xs: 1, md: 1.5 }, px: { xs: 0.5, md: 1 }, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "11px", md: "12px" } }}>
                          No trips available
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </>}
          </Box>
        )} */}

        <Box sx={{ height: "16px" }} />
      </Box>
    </Box >
  );
}
function RenderTimeDiff({ from, to }: { from?: string; to?: string }) {
  if (!from || !to) {
    return undefined;
  }
  let {seconds} = DateTime.fromISO(to)!.diff(DateTime.fromISO(from), 'seconds').toObject();

  if (seconds) {
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;

    seconds = Math.floor(seconds);

    return <>
      {`${hours > 0 ? `${hours}h ` : ''}
      ${minutes > 0 ? `${minutes}m ` : ''}
      ${seconds > 0 && hours === 0 && minutes === 0 ? `${seconds}s` : ''}`.trim()}
    </>;
  }
}

const copyLocation = async (location: string) => {
  try {
    enqueueSnackbar("Location copied to clipboard.");
    await navigator.clipboard.writeText(location);
  } catch (error) {
    enqueueSnackbar("Failed to copy location.", { variant: "error" });
  }
}

function SafetyTripRow({
  trip, index, hoveredTrip, setHoveredTrip, safetyTrips, timezone
}: {
  trip: SafetyTripFull,
  index: number,
  hoveredTrip: SafetyTripFull | undefined,
  setHoveredTrip: (a: SafetyTripFull | undefined) => void,
  safetyTrips: SafetyTripFull[],
  timezone: string
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return <Box
    sx={{
      position: "relative",
      borderBottom: "1px solid #e0e0e0",
      cursor: "pointer",
      "&:hover": { backgroundColor: "#f5f5f5" },
      pt: 0.5,
      pb: 0.5,
      "&:hover .trip-marker": { color: "white", backgroundColor: "#3d93e9ff" },
      "& .trip-marker": hoveredTrip === trip ? { color: "white", backgroundColor: "#3d93e9ff" } : undefined,
      "&": hoveredTrip === trip ? { backgroundColor: "#f5f5f5" } : undefined,
    }}
    onClick={() => {
      if (hoveredTrip === trip) {
        setHoveredTrip(undefined);
      } else {
        setHoveredTrip(trip)
      }
    }}
  >
    {index !== 0 && safetyTrips[index - 1].end?.eventTime && <Box sx={{ position: "absolute", top: "-0px", left: "50%", transform: "translateX(-50%) translateY(-50%)", background: "white", fontSize: "13px", borderRadius: "10px", border: "1px solid #DDD", p: 0.25, pl: 0.35, pr: 0.35 }}>
      <RenderTimeDiff from={safetyTrips[index - 1].end?.eventTime} to ={trip.start.eventTime} />
    </Box>}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, "&:hover .trip-marker": { backgroundColor: "#3d93e9ff" } }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "-2px" }}>
        {trip.end?.eventTime && <Box className="trip-marker" sx={{ width: "20px", height: "20px", backgroundColor: "#D4D4D4", borderRadius: "100%", textAlign: "center", lineHeight: "20px", transition: "background-color 0.2s" }}>
          B
        </Box>}
        {trip.end?.eventTime && <Box className="trip-marker" sx={{ lineHeight: "8px", width: "3px", height: "8px", backgroundColor: "#D4D4D4", transition: "background-color 0.2s" }} />}
        <Box className="trip-marker" sx={{ width: "20px", height: "20px", backgroundColor: "#D4D4D4", borderRadius: "100%", textAlign: "center", lineHeight: "20px", transition: "background-color 0.2s" }}>
          A
        </Box>
      </Box>

      {/* Trip Details */}
      <Box sx={{ flex: 1, minWidth: 0, gap: "4px", display: "flex", flexDirection: "column" }}>
        {trip.end?.eventTime && <Box>
          <Typography variant="body2" sx={{ fontSize: "11px", fontWeight: 600 }}>
            {trip.end.location}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "12px" }}>
            {formatDatetimeForPreview(trip.end?.eventTime, timezone)} <Box display="inline" sx={{ color: "#777" }}>(<RenderTimeDiff from={trip.start.eventTime} to={trip.end.eventTime} />)</Box>
          </Typography>
        </Box>}

        <Box>
          <Typography variant="body2" sx={{ fontSize: "11px", fontWeight: 600 }}>
            {trip.start.location}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "12px" }}>
            {formatDatetimeForPreview(trip.start.eventTime, timezone)}
          </Typography>
        </Box>
      </Box>

      <Box>
        <IconButton onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClick(e);
        }}>
          <Iconify icon="solar:menu-dots-bold" rotate={45} />
        </IconButton>
      </Box>

      {open && <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={(e) => {e.preventDefault(); e.stopPropagation(); handleClose(); copyLocation(`${trip.start.posLat},${trip.start.posLong}`)}}>Copy From Location</MenuItem>
        {trip.end && <MenuItem onClick={(e) => {e.preventDefault(); e.stopPropagation(); handleClose(); copyLocation(trip.end ? `${trip.end.posLat},${trip.end.posLong}` : '')}}>Copy To Location</MenuItem>}
        <MenuItem onClick={(e) => {e.preventDefault(); e.stopPropagation(); handleClose();}} href={`https://maps.google.com/?q=${trip.start.posLat},${trip.start.posLong}`} target="_blank">Open From w Google Maps</MenuItem>
        {trip.end && <MenuItem onClick={(e) => {e.preventDefault(); e.stopPropagation(); handleClose();}} href={`https://maps.google.com/?q=${trip.end?.posLat},${trip.end?.posLong}`} target="_blank">Open To w Google Maps</MenuItem>}
      </Menu>}
    </Box>
  </Box>
}